import { useFeatureValue } from "@growthbook/growthbook-react";
import { Button } from "components/atoms/button/button";
import { TextArea } from "components/form-component/text-area/text-area";
import { GrowthbookFeatures } from "components/growthbook/growthbook.types";
import { EyeClosed } from "components/icons/eye-closed";
import { ClassRating, getRatingObjByValue } from "constants/rate-classes";
import Image from "next/legacy/image";
import React, { useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { Tag } from "services/api/class-feedback/classFeedbackService.types";
import trackEvent from "services/mixpanel/mixpanel";
import { userFeedbackEvents } from "services/mixpanel/user-feedback-events";
import { IUserFeedbackFeatureValue } from "../rate-classes.types";
import {
  RateClassChip,
  RateClassChipsContainer,
  RateClassModalContent,
  RateClassModalCTAContainer,
  RateClassModalFooter,
  RateClassModalFooterNote,
  RateClassModalHeader,
  RateClassModalImgContainer,
  RateClassModalNote,
  RateClassModalSubHeading,
  TextAreaWrapper
} from "./rate-class-modal.styles";
import { IRateClassDetailedFormProps } from "./rate-class-modal.types";

const RateClassDetailedForm: React.FC<IRateClassDetailedFormProps> = ({
  selectedRating,
  onSubmit,
  onSkip,
  metaData,
  data: { subject, liveClass, modalSource }
}) => {
  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    getValues,
    formState: { dirtyFields }
  } = useForm<{ comment: string; tags: string[] }>({
    defaultValues: { comment: "", tags: [] }
  });
  const isCommentDirtyField = useRef(false);

  const selectedTags = watch("tags");
  const comment = watch("comment");

  const sendFreeTextTrackEvent = () => {
    trackEvent({
      eventName: userFeedbackEvents.liveClassDetailedFeedbackFreeText,
      payload: {
        classID: liveClass.id,
        subject: subject.name,
        incomingSource: modalSource,
        feedbackType: getRatingObjByValue(selectedRating)?.key
      }
    });
  };

  useEffect(() => {
    if (!isCommentDirtyField.current && dirtyFields.comment) {
      isCommentDirtyField.current = true;
      sendFreeTextTrackEvent();
    }
  }, [dirtyFields]);

  useEffect(() => {
    register("tags");
  }, [register]);

  useEffect(() => {
    trackEvent({
      eventName: userFeedbackEvents.liveClassDetailedFeedbackPageViewed,
      payload: {
        classId: liveClass.id,
        subject: subject.name,
        feedbackType: getRatingObjByValue(selectedRating)?.key,
        incomingSource: modalSource
      }
    });
  }, [subject, liveClass, selectedRating, modalSource]);

  const handleTagSelection = (tag: Tag) => {
    const currentSelectedTags = getValues().tags;
    const isTagAlreadySelected = currentSelectedTags.find(
      (tagId) => tag.id === tagId
    );
    if (isTagAlreadySelected) {
      return setValue(
        "tags",
        currentSelectedTags.filter((tagId) => tag.id !== tagId)
      );
    }
    setValue("tags", currentSelectedTags.concat(tag.id));
  };

  const tags = metaData.tagsByRating[selectedRating];

  const isValid = comment.length > 0 || selectedTags.length > 0;
  const featureValue = useFeatureValue(GrowthbookFeatures.UserFeedback, null);
  if (featureValue) {
    const {
      img,
      tag_header,
      comment_header,
      comment_hint,
      headerSubHeading,
      headerNote
    } = getContentBySelectedRating(selectedRating, featureValue);

    return (
      <form onSubmit={handleSubmit(onSubmit)}>
        <RateClassModalHeader>
          <RateClassModalImgContainer height={80} width={160}>
            <Image
              src={`/assets/kelas-live/rate-classes/${img}`}
              layout="fill"
              alt="Detailed rating"
            />
          </RateClassModalImgContainer>
          <RateClassModalSubHeading>
            {headerSubHeading}
          </RateClassModalSubHeading>
          <RateClassModalNote>{headerNote}</RateClassModalNote>
        </RateClassModalHeader>
        <RateClassModalContent>
          <div>
            <RateClassModalSubHeading>{tag_header}</RateClassModalSubHeading>
            <RateClassChipsContainer>
              {tags.map((tag) => (
                <RateClassChip
                  key={tag.id}
                  selected={selectedTags.includes(tag.id)}
                  onClick={() => handleTagSelection(tag)}
                >
                  {tag.title}
                </RateClassChip>
              ))}
            </RateClassChipsContainer>
          </div>
          <div>
            <RateClassModalSubHeading>
              {comment_header}
            </RateClassModalSubHeading>
            <TextAreaWrapper>
              <TextArea
                name="comment"
                maxLength={300}
                control={control}
                placeholder={comment_hint}
                showWordCount
              />
            </TextAreaWrapper>
          </div>
        </RateClassModalContent>
        <RateClassModalFooter>
          <RateClassModalCTAContainer>
            <Button variant="ghost" type="button" onClick={onSkip}>
              Lewati
            </Button>
            <Button variant="primary" disabled={!isValid} type="submit">
              Kirim masukan
            </Button>
          </RateClassModalCTAContainer>
          <RateClassModalFooterNote>
            <EyeClosed width={24} height={24} />{" "}
            <span>Namamu nggak akan keliatan, kok</span>
          </RateClassModalFooterNote>
        </RateClassModalFooter>
      </form>
    );
  }
  return <></>;
};

const getContentBySelectedRating = (
  selectedRating: ClassRating,
  content: IUserFeedbackFeatureValue
) => {
  const ratingSpecificContent =
    content.user_feedback_var_key.detail_content.find(
      (content) => content.rating_level === selectedRating
    );
  switch (selectedRating) {
    case ClassRating.POSITIVE: {
      return {
        img: "rating-positive-detailed.svg",
        headerSubHeading: "Kelasnya seru!",
        headerNote: "Kasih masukan ya, biar kelasnya makin seru.",
        ...ratingSpecificContent
      };
    }

    case ClassRating.NEUTRAL: {
      return {
        img: "rating-neutral-detailed.svg",
        headerSubHeading: "Kelasnya biasa aja",
        headerNote: "Kasih masukan ya, biar bisa diperbaiki",
        ...ratingSpecificContent
      };
    }

    case ClassRating.NEGATIVE: {
      return {
        img: "rating-negative-detailed.svg",
        headerSubHeading: "Kelasnya ngebosenin",
        headerNote: "Kasih masukan ya, biar bisa diperbaiki",
        ...ratingSpecificContent
      };
    }
  }
};

export default RateClassDetailedForm;
